// Wrapped in an IIFE: the block can appear more than once and this classic script would run again.
(() => {
  /**
   * Calculates the next pickup date for the pickup-info block.
   * Pure function so it can be checked outside the browser (tests/pickup-info.test.mjs).
   *
   * FOC-23: like Pickeasy, the deadline to pick up on day D is the cutoff of the previous working day
   * (an enabled pickup day that isn't closed): Friday and Saturday at `cutoffWeekend` (14:00), the
   * other days at `cutoff` (16:00). Sunday is not a working day, so Monday closes on Saturday at 14:00.
   *
   * @param {object} options
   * @param {string} options.cutoff - Cutoff time as HH:MM (24 h), Monday to Thursday
   * @param {string} [options.cutoffWeekend] - Cutoff on Friday and Saturday (HH:MM); defaults to `cutoff`
   * @param {number[]} options.days - Enabled JS weekdays (0 = Sunday … 6 = Saturday)
   * @param {string[]} options.closed - Closed dates as YYYY-MM-DD
   * @param {{year: number, month: number, day: number, hour: number, minute: number}} options.now - Store-local date and time
   * @returns {{date: Date, offset: number, deadlineToday: boolean, cutoff: string} | null} UTC midnight of the pickup date, its offset in days, whether the deadline is today (then `cutoff` is that deadline's time) or a later day
   */
  function getNextPickupDate({ cutoff, cutoffWeekend = '', days, closed, now }) {
    const toMinutes = (/** @type {string} */ value) => {
      const match = /^(\d{1,2}):(\d{2})$/.exec(value);
      return match ? Number(match[1]) * 60 + Number(match[2]) : null;
    };

    if (toMinutes(cutoff) === null) return null;
    const weekendCutoff = toMinutes(cutoffWeekend) === null ? cutoff : cutoffWeekend;

    const nowMinutes = now.hour * 60 + now.minute;
    const base = Date.UTC(now.year, now.month - 1, now.day);
    const dayInMs = 24 * 60 * 60 * 1000;
    const dateAt = (/** @type {number} */ offset) => new Date(base + offset * dayInMs);
    const isWorkingDay = (/** @type {Date} */ date) =>
      days.includes(date.getUTCDay()) && !closed.includes(date.toISOString().slice(0, 10));
    const cutoffFor = (/** @type {Date} */ date) => (date.getUTCDay() === 5 || date.getUTCDay() === 6 ? weekendCutoff : cutoff);

    for (let offset = 1; offset <= 61; offset++) {
      const date = dateAt(offset);
      if (!isWorkingDay(date)) continue;

      // Deadline day: the previous working day (at most a week back).
      let leadOffset = offset - 1;
      while (leadOffset > offset - 8 && !isWorkingDay(dateAt(leadOffset))) leadOffset--;
      if (leadOffset < 0) continue; // The deadline already passed (e.g. Monday on a Sunday).

      const leadCutoff = cutoffFor(dateAt(leadOffset));
      if (leadOffset === 0 && nowMinutes >= /** @type {number} */ (toMinutes(leadCutoff))) continue;

      return { date, offset, deadlineToday: leadOffset === 0, cutoff: leadCutoff };
    }

    return null;
  }

  /**
   * Returns the current date and time in the store's time zone, never the device's.
   * @returns {{year: number, month: number, day: number, hour: number, minute: number}}
   */
  function getStoreNow() {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Argentina/Buenos_Aires',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());

    /** @type {Record<string, number>} */
    const values = {};
    for (const part of parts) {
      if (part.type !== 'literal') values[part.type] = Number(part.value);
    }

    return {
      year: values.year ?? 0,
      month: values.month ?? 0,
      day: values.day ?? 0,
      hour: values.hour ?? 0,
      minute: values.minute ?? 0,
    };
  }

  /**
   * Formats a pickup date as "jueves 23/10".
   * @param {Date} date - UTC midnight of the pickup date
   * @param {string} locale
   * @returns {string}
   */
  function formatPickupDate(date, locale) {
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(date);
    return `${weekday} ${date.getUTCDate()}/${date.getUTCMonth() + 1}`;
  }

  /**
   * Picks the time window for the pickup weekday.
   * @param {Date} date - UTC midnight of the pickup date
   * @param {string} windows - Seven windows separated by "|", indexed by JS weekday (0 = Sunday … 6 = Saturday)
   * @returns {string} '' if there is no window for that day
   */
  function getWindowForDate(date, windows) {
    const list = windows.split('|');
    return (list[date.getUTCDay()] || '').trim();
  }

  /**
   * Replaces the server-rendered fallback with the next real pickup date.
   * Without JavaScript the fallback text stays as is.
   */
  class PickupInfo extends HTMLElement {
    connectedCallback() {
      const text = this.querySelector('.pickup-info__text');
      if (!text) return;

      const {
        cutoff = '',
        cutoffWeekend = '',
        days = '',
        closed = '',
        templateDate = '',
        templateTomorrow = '',
        templateAfterCutoff = '',
        windows = '',
      } = this.dataset;

      const result = getNextPickupDate({
        cutoff: cutoff.trim(),
        cutoffWeekend: cutoffWeekend.trim(),
        days: days
          .split(',')
          .filter((day) => day !== '')
          .map(Number),
        closed: closed.split(',').filter((date) => date !== ''),
        now: getStoreNow(),
      });
      if (!result) return;

      // FOC-23: the time window depends on the pickup weekday (Mon–Fri 17–21, Sat 10–14).
      const pickupWindow = getWindowForDate(result.date, windows);
      if (!pickupWindow) return;

      // Deadline today: «Pedí antes de las HH:MM y retirala desde mañana / el <día>».
      // Deadline on a later day: ordering today is enough, «Pedí hoy y retirala desde el <día>».
      const locale = document.documentElement.lang || 'es';

      // «mañana sábado 24/10»: también se nombra el día y la fecha (FOC-23).
      if (result.deadlineToday && result.offset === 1) {
        if (templateTomorrow) {
          text.textContent = templateTomorrow
            .replace('__CUTOFF__', result.cutoff)
            .replace('__DATE__', formatPickupDate(result.date, locale))
            .replace('__WINDOW__', pickupWindow);
        }
        return;
      }

      const template = result.deadlineToday ? templateDate : templateAfterCutoff;
      if (!template) return;

      text.textContent = template
        .replace('__CUTOFF__', result.cutoff)
        .replace('__DATE__', formatPickupDate(result.date, locale))
        .replace('__WINDOW__', pickupWindow);
    }
  }

  // Exposed for checks outside the browser; not used by the theme.
  PickupInfo.getNextPickupDate = getNextPickupDate;
  PickupInfo.formatPickupDate = formatPickupDate;
  PickupInfo.getWindowForDate = getWindowForDate;

  if (!customElements.get('pickup-info')) {
    customElements.define('pickup-info', PickupInfo);
  }
})();
